import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import ImageUpload from './ImageUpload';
import { isRequired } from '../../utils/validation';

const ProjectForm = ({ project = null, onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: 'photography',
    description: '',
    coverImage: '',
    images: [],
    clientName: '',
    date: '',
    featured: false,
    process: '',
    results: '',
    testimonialText: '',
    testimonialAuthor: '',
    sortOrder: 0,
    published: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        slug: project.slug || '',
        category: project.category || 'photography',
        description: project.description || '',
        coverImage: project.coverImage || '',
        images: project.images || [],
        clientName: project.clientName || '',
        date: project.date ? project.date.split('T')[0] : '',
        featured: project.featured || false,
        process: project.process || '',
        results: project.results || '',
        testimonialText: project.testimonial?.text || '',
        testimonialAuthor: project.testimonial?.author || '',
        sortOrder: project.sortOrder || 0,
        published: project.published !== undefined ? project.published : true,
      });
    }
  }, [project]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'title') {
      setFormData((prev) => ({
        ...prev,
        title: value,
        slug: generateSlug(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

const handleCoverImageUpload = (url) => {
  const imageUrl = Array.isArray(url) ? url[0] : url;
  setFormData((prev) => ({ ...prev, coverImage: imageUrl }));
  if (errors.coverImage) {
    setErrors((prev) => ({ ...prev, coverImage: null }));
  }
};

  const handleImagesUpload = (urls) => {
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...urls]
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!isRequired(formData.title)) {
      newErrors.title = 'Title is required';
    }

    if (!isRequired(formData.slug)) {
      newErrors.slug = 'Slug is required';
    }

    if (!isRequired(formData.category)) {
      newErrors.category = 'Category is required';
    }

    if (!isRequired(formData.description)) {
      newErrors.description = 'Description is required';
    }

    if (!isRequired(formData.coverImage)) {
      newErrors.coverImage = 'Cover image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData = {
      ...formData,
      testimonial: formData.testimonialText
        ? {
            text: formData.testimonialText,
            author: formData.testimonialAuthor,
          }
        : undefined,
    };

    delete submitData.testimonialText;
    delete submitData.testimonialAuthor;

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title and Slug */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter project title"
          />
          {errors.title && (
            <p className="text-red-500 text-sm mt-1">{errors.title}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Slug <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${
              errors.slug ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="project-slug"
          />
          {errors.slug && (
            <p className="text-red-500 text-sm mt-1">{errors.slug}</p>
          )}
        </div>
      </div>

      {/* Category and Client Name */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${
              errors.category ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="photography">Photography</option>
            <option value="writing">Writing</option>
            <option value="social-media">Social Media</option>
          </select>
          {errors.category && (
            <p className="text-red-500 text-sm mt-1">{errors.category}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Client Name
          </label>
          <input
            type="text"
            name="clientName"
            value={formData.clientName}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="Enter client name"
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="4"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter project description"
        />
        {errors.description && (
          <p className="text-red-500 text-sm mt-1">{errors.description}</p>
        )}
      </div>

      {/* Cover Image */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Cover Image <span className="text-red-500">*</span>
        </label>
        <ImageUpload
          onUpload={handleCoverImageUpload}
          existingImage={formData.coverImage}
        />
        {errors.coverImage && (
          <p className="text-red-500 text-sm mt-1">{errors.coverImage}</p>
        )}
      </div>

      {/* Gallery Images */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Gallery Images {formData.images.length > 0 && `(${formData.images.length})`}
        </label>
        <ImageUpload onUpload={handleImagesUpload} multiple />
        {formData.images.length > 0 && (
          <div className="grid grid-cols-4 gap-4 mt-4">
            {formData.images.map((img, index) => (
              <div key={index} className="relative group">
                <img
                  src={img}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-24 object-cover rounded"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Date, Sort Order, and Checkboxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date
          </label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sort Order
          </label>
          <input
            type="number"
            name="sortOrder"
            value={formData.sortOrder}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="0"
          />
        </div>
      </div>

      <div className="flex gap-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            name="featured"
            checked={formData.featured}
            onChange={handleChange}
            className="w-4 h-4 text-accent focus:ring-accent border-gray-300 rounded"
          />
          <span className="text-sm font-medium text-gray-700">Featured</span>
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            name="published"
            checked={formData.published}
            onChange={handleChange}
            className="w-4 h-4 text-accent focus:ring-accent border-gray-300 rounded"
          />
          <span className="text-sm font-medium text-gray-700">Published</span>
        </label>
      </div>

      {/* Process */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Process
        </label>
        <textarea
          name="process"
          value={formData.process}
          onChange={handleChange}
          rows="4"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder="Describe the process and behind-the-scenes details"
        />
      </div>

      {/* Results */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Results
        </label>
        <textarea
          name="results"
          value={formData.results}
          onChange={handleChange}
          rows="4"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder="Describe the outcomes and client feedback"
        />
      </div>

      {/* Testimonial */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-heading font-semibold text-secondary mb-4">
          Testimonial (Optional)
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Testimonial Text
            </label>
            <textarea
              name="testimonialText"
              value={formData.testimonialText}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="Enter testimonial text"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Testimonial Author
            </label>
            <input
              type="text"
              name="testimonialAuthor"
              value={formData.testimonialAuthor}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="Enter author name"
            />
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-4 border-t pt-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? 'Saving...' : project ? 'Update Project' : 'Create Project'}
        </Button>
      </div>
    </form>
  );
};

export default ProjectForm;
